// Editing rules for a dispatch plan, kept apart from the page so they can be
// tested without a DOM. The page holds two things: the plan as the server last
// returned it, and a working copy the dispatcher edits. Edits only ever touch
// the working copy; "Revalidate" sends it to the server, which saves and
// re-checks it and becomes the new baseline.

import type {
  DispatchPlan,
  PlanDocument,
  PlanOrder,
  PlanRoute,
  PlanTruck,
  PlanWindow,
  RosterVehicle,
} from "../types";

// The parts of a plan the dispatcher can change.
export interface WorkingPlan {
  plan_date: string;
  document: PlanDocument;
}

// Orders move between trips one at a time; stops (a ZIP's orders together)
// reorder within a trip. Setting the order by hand locks it: recalibration
// then keeps it, until unlockSequence hands it back to the optimizer. An
// order taken off the plan (takeOff) waits in `unrouted` until putBack puts
// it on a route. Trips belong to trucks: a truck's carrier, vehicle and driver
// (setCarrier, setVehicle, setDriver) are its trips' (a truck set to 3PL hands
// them all to the 3PL carrier), setDeparture is when it leaves on its first
// trip (minutes from midnight; a new truck leaves at 8:30), addTrip adds its last trip, addTruck a new
// truck (with a first trip when `routeId` is given), and only an empty trip,
// or a truck whose trips are all empty, is removed.
// setAddress gives an order the dispatcher's delivery address (null or
// blank: the system's), setWindow its delivery window (null: the system's),
// setNotes its delivery notes for the driver (null: the system's; "": none).
// moveOrder's `index`/`toIndex`, takeOff's `index` and reorderInStop's
// `from`/`to` are positions in `route.orders`; putBack's `index` is one in
// `unrouted`; moveStop's `from`/`to` are stop numbers.
export type PlanEdit =
  | { kind: "moveOrder"; fromRouteId: string; index: number; toRouteId: string; toIndex?: number }
  | { kind: "moveStop"; routeId: string; from: number; to: number }
  | { kind: "reorderInStop"; routeId: string; from: number; to: number }
  | { kind: "unlockSequence"; routeId: string }
  | { kind: "setCarrier"; truckId: string; carrier: "fleet" | "3pl" }
  | { kind: "setVehicle"; truckId: string; vehicleId: string | null }
  | { kind: "setDriver"; truckId: string; driverId: string | null }
  | { kind: "setDeparture"; truckId: string; departMin: number }
  | { kind: "takeOff"; routeId: string; index: number }
  | { kind: "putBack"; index: number; toRouteId: string }
  | { kind: "putBackMany"; orderNos: string[]; toRouteId: string }
  | { kind: "addTrip"; truckId: string; routeId: string }
  | { kind: "addTruck"; truckId: string; routeId: string | null }
  | { kind: "removeTrip"; routeId: string }
  | { kind: "removeTruck"; truckId: string }
  | { kind: "setAddress"; orderNo: string; address: string | null }
  | { kind: "setWindow"; orderNo: string; window: PlanWindow | null }
  | { kind: "setNotes"; orderNo: string; notes: string | null };

// Same truck colours as the results map (serialization/to_dto.py
// _OWN_PALETTE), so T1 looks the same on both pages.
const TRUCK_COLORS = ["#D4A017", "#3A75C4", "#2C9A6E", "#B5494D", "#7B5DBE", "#5C7A9C"];

// A physical stop: consecutive orders for one ZIP, with their positions.
export interface RouteStop {
  zip: string;
  orders: { order: PlanOrder; index: number }[];
}

export type RequestState = "idle" | "loading" | "validating" | "publishing";

// What the page shows as the plan's state. Server publish states win, then an
// in-flight request, then unsaved edits, then the last validation's verdict.
export type PlanPhase =
  | "draft"
  | "modified"
  | "validating"
  | "valid"
  | "invalid"
  | "validation_error"
  | "publishing"
  | "published"
  | "partially_published"
  | "publish_failed"
  | "closed";

export function workingFrom(plan: DispatchPlan): WorkingPlan {
  return { plan_date: plan.plan_date, document: structuredClone(plan.document) };
}

export function routeStops(route: PlanRoute): RouteStop[] {
  const stops: RouteStop[] = [];
  route.orders.forEach((order, index) => {
    const last = stops[stops.length - 1];
    if (last && last.zip === order.zip) last.orders.push({ order, index });
    else stops.push({ zip: order.zip, orders: [{ order, index }] });
  });
  return stops;
}

// Where an order dropped at `at` really lands: beside its own ZIP when the
// trip already serves it (one stop, never two), else at `at` nudged out of
// any other ZIP's stop so that stop stays whole.
function landingIndex(orders: PlanOrder[], order: PlanOrder, at?: number): number {
  const sameZip = orders.map((o) => o.zip).lastIndexOf(order.zip);
  if (sameZip >= 0) return sameZip + 1;
  let k = Math.max(0, Math.min(at ?? orders.length, orders.length));
  while (k > 0 && k < orders.length && orders[k - 1].zip === orders[k].zip) k++;
  return k;
}

// Add `order` to `orders`: folded into the same order's item when it is
// there already (the rest of an order split across trips), else at `at`.
function join(orders: PlanOrder[], order: PlanOrder, at: number): void {
  const same = orders.findIndex((o) => o.order_no === order.order_no);
  if (same >= 0) {
    const existing = orders[same];
    orders[same] = { ...existing, shipment_ids: [...existing.shipment_ids, ...order.shipment_ids] };
  } else {
    orders.splice(at, 0, order);
  }
}

export function applyEdit(doc: PlanDocument, edit: PlanEdit): PlanDocument {
  const routes = doc.routes.map((r) => ({ ...r, orders: [...r.orders] }));
  const trucks = doc.trucks.map((t) => ({ ...t }));
  const unrouted = [...doc.unrouted];
  const find = (id: string): PlanRoute | undefined => routes.find((r) => r.route_id === id);
  const truck = (id: string): PlanTruck | undefined => trucks.find((t) => t.truck_id === id);
  // A trip runs as its truck does: carrier, vehicle and driver (the server's rule too).
  const onTruck = (route: PlanRoute, t: PlanTruck) =>
    Object.assign(route, {
      truck_id: t.truck_id,
      carrier: t.carrier,
      vehicle_id: t.vehicle_id,
      driver_id: t.driver_id,
    });

  switch (edit.kind) {
    case "moveOrder": {
      const source = find(edit.fromRouteId);
      const target = find(edit.toRouteId);
      if (!source || !target || source === target) return doc;
      const [order] = source.orders.splice(edit.index, 1);
      if (!order) return doc;
      join(target.orders, order, landingIndex(target.orders, order, edit.toIndex));
      return { ...doc, routes, unrouted };
    }
    case "takeOff": {
      const [order] = find(edit.routeId)?.orders.splice(edit.index, 1) ?? [];
      if (!order) return doc;
      join(unrouted, order, unrouted.length);
      return { ...doc, routes, unrouted };
    }
    case "putBack": {
      const target = find(edit.toRouteId);
      const [order] = unrouted.splice(edit.index, 1);
      if (!target || !order) return doc;
      join(target.orders, order, landingIndex(target.orders, order));
      return { ...doc, routes, unrouted };
    }
    case "putBackMany": {
      // In the pool's order, each with its ZIP's stop when the trip has one, else last.
      const target = find(edit.toRouteId);
      if (!target) return doc;
      const picked = new Set(edit.orderNos);
      for (const order of unrouted.filter((o) => picked.has(o.order_no))) {
        join(target.orders, order, landingIndex(target.orders, order));
      }
      return { ...doc, routes, unrouted: unrouted.filter((o) => !picked.has(o.order_no)) };
    }
    case "setAddress": {
      const addresses = { ...doc.addresses };
      const address = edit.address?.trim();
      if (address) addresses[edit.orderNo] = address;
      else delete addresses[edit.orderNo];
      return { ...doc, addresses };
    }
    case "setNotes": {
      const notes = { ...doc.notes };
      if (edit.notes === null) delete notes[edit.orderNo];
      else notes[edit.orderNo] = edit.notes.trim();
      return { ...doc, notes };
    }
    case "setWindow": {
      const windows = { ...doc.windows };
      if (edit.window) windows[edit.orderNo] = edit.window;
      else delete windows[edit.orderNo];
      return { ...doc, windows };
    }
    case "addTrip": {
      const t = truck(edit.truckId);
      if (!t) return doc;
      routes.push(onTruck(emptyTrip(edit.routeId), t));
      return { ...doc, trucks, routes, unrouted };
    }
    case "addTruck": {
      const t: PlanTruck = {
        truck_id: edit.truckId,
        carrier: "fleet",
        vehicle_id: null,
        driver_id: null,
        trailer_type: "26ft",
        depart_min: null,
      };
      trucks.push(t);
      if (edit.routeId) routes.push(onTruck(emptyTrip(edit.routeId), t));
      return { ...doc, trucks, routes, unrouted };
    }
    case "removeTrip": {
      if (find(edit.routeId)?.orders.length !== 0) return doc;
      const rest = routes.filter((r) => r.route_id !== edit.routeId);
      return { ...doc, trucks, routes: rest, unrouted };
    }
    case "removeTruck": {
      const trips = routes.filter((r) => r.truck_id === edit.truckId);
      if (trips.some((r) => r.orders.length > 0)) return doc;
      return {
        ...doc,
        trucks: trucks.filter((t) => t.truck_id !== edit.truckId),
        routes: routes.filter((r) => r.truck_id !== edit.truckId),
        unrouted,
      };
    }
    case "moveStop": {
      const route = find(edit.routeId);
      if (!route || edit.from === edit.to) return doc;
      const stops = routeStops(route);
      const to = Math.max(0, Math.min(edit.to, stops.length - 1));
      const [stop] = stops.splice(edit.from, 1);
      if (!stop) return doc;
      stops.splice(to, 0, stop);
      route.orders = stops.flatMap((s) => s.orders.map((o) => o.order));
      route.sequence_locked = true;
      return { ...doc, routes, unrouted };
    }
    case "reorderInStop": {
      const route = find(edit.routeId);
      const from = route?.orders[edit.from];
      const to = route?.orders[edit.to];
      if (!route || !from || !to || from.zip !== to.zip || edit.from === edit.to) return doc;
      const between = route.orders.slice(
        Math.min(edit.from, edit.to),
        Math.max(edit.from, edit.to) + 1,
      );
      if (between.some((o) => o.zip !== from.zip)) return doc;
      route.orders.splice(edit.from, 1);
      route.orders.splice(edit.to, 0, from);
      route.sequence_locked = true;
      return { ...doc, routes, unrouted };
    }
    case "unlockSequence": {
      const route = find(edit.routeId);
      if (!route) return doc;
      route.sequence_locked = false;
      return { ...doc, routes, unrouted };
    }
    case "setCarrier": {
      const t = truck(edit.truckId);
      if (!t) return doc;
      t.carrier = edit.carrier;
      // 3PL has no vehicle or driver of ours; back on the fleet, they are
      // chosen. The departure stays either way until the dispatcher moves it.
      if (edit.carrier === "3pl") {
        Object.assign(t, { vehicle_id: null, driver_id: null });
      }
      for (const r of routes) if (r.truck_id === t.truck_id) onTruck(r, t);
      return { ...doc, trucks, routes, unrouted };
    }
    case "setVehicle":
    case "setDriver": {
      const t = truck(edit.truckId);
      if (!t) return doc;
      if (edit.kind === "setVehicle") t.vehicle_id = edit.vehicleId;
      else t.driver_id = edit.driverId;
      for (const r of routes) if (r.truck_id === t.truck_id) onTruck(r, t);
      return { ...doc, trucks, routes, unrouted };
    }
    case "setDeparture": {
      const t = truck(edit.truckId);
      if (!t) return doc;
      t.depart_min = edit.departMin;
      return { ...doc, trucks, routes, unrouted };
    }
  }
}

function emptyTrip(routeId: string): PlanRoute {
  return {
    route_id: routeId,
    carrier: "fleet",
    truck_id: null,
    vehicle_id: null,
    driver_id: null,
    sequence_locked: false,
    orders: [],
  };
}

// The id for a trip the dispatcher adds to a truck: "T<n>-trip<k>" after any
// the plan has or ever sent (a publish keys the route by it, so it's never
// reused).
export function nextTripId(plan: DispatchPlan, doc: PlanDocument, truckId: string): string {
  const used = [
    ...doc.routes.map((r) => r.route_id),
    ...Object.keys(plan.publication?.routes ?? {}),
  ];
  const ks = used.flatMap((id) => {
    const m = /^(T\d+)-trip(\d+)$/.exec(id);
    return m && m[1] === truckId ? [Number(m[2])] : [];
  });
  return `${truckId}-trip${ks.length ? Math.max(...ks) + 1 : 0}`;
}

// The next truck number the plan doesn't have, nor `taken` (trucks with a 3PL
// booking on record, which would show as the new truck's).
export function nextTruckId(doc: PlanDocument, taken: string[] = []): string {
  const ids = [...doc.trucks.map((t) => t.truck_id), ...taken];
  return `T${Math.max(0, ...ids.map(truckNumber)) + 1}`;
}

function truckNumber(truckId: string): number {
  return Number(/^T(\d+)$/.exec(truckId)?.[1] ?? 0);
}

// A truck's colour, by its number: adding or removing other trucks never
// changes it.
export function truckColor(truckId: string): string {
  return TRUCK_COLORS[Math.max(0, truckNumber(truckId) - 1) % TRUCK_COLORS.length];
}

// The vehicles `truckId` may take: its own and those no other truck has
// (null: a truck not added yet).
export function freeVehicles(
  doc: PlanDocument,
  vehicles: RosterVehicle[],
  truckId: string | null,
): RosterVehicle[] {
  const taken = new Set(
    doc.trucks.filter((t) => t.truck_id !== truckId).map((t) => t.vehicle_id),
  );
  return vehicles.filter((v) => !taken.has(v.id));
}

// A trip's truck and its place among the truck's trips (1-based); null for a
// route the plan doesn't have.
export function tripOf(doc: PlanDocument, routeId: string): { truckId: string; n: number } | null {
  const route = doc.routes.find((r) => r.route_id === routeId);
  if (!route?.truck_id) return null;
  const trips = doc.routes.filter((r) => r.truck_id === route.truck_id);
  return { truckId: route.truck_id, n: trips.indexOf(route) + 1 };
}

// The edit that moves the order at `index` one place earlier (-1) or later
// (+1): past its neighbour in the same stop, else its whole stop past the
// neighbouring stop (a ZIP's orders stay together). Null at either end.
export function stepEdit(route: PlanRoute, index: number, delta: -1 | 1): PlanEdit | null {
  const neighbour = route.orders[index + delta];
  if (!neighbour) return null;
  if (neighbour.zip === route.orders[index].zip) {
    return { kind: "reorderInStop", routeId: route.route_id, from: index, to: index + delta };
  }
  const stops = routeStops(route);
  const from = stops.findIndex((s) => s.zip === route.orders[index].zip);
  return { kind: "moveStop", routeId: route.route_id, from, to: from + delta };
}

// An empty trip no driver has can be removed; one with its driver is
// emptied, and publishing withdraws it (the server's rule too).
export function removableTrip(plan: DispatchPlan, doc: PlanDocument, routeId: string): boolean {
  const route = doc.routes.find((r) => r.route_id === routeId);
  return (
    route !== undefined &&
    route.orders.length === 0 &&
    plan.publication?.routes[routeId]?.status !== "sent"
  );
}

export function removableTruck(plan: DispatchPlan, doc: PlanDocument, truckId: string): boolean {
  return doc.routes
    .filter((r) => r.truck_id === truckId)
    .every((r) => removableTrip(plan, doc, r.route_id));
}

// Compared by value, so undoing an edit makes the plan clean again.
export function isDirty(working: WorkingPlan, server: DispatchPlan): boolean {
  return (
    working.plan_date !== server.plan_date ||
    JSON.stringify(working.document) !== JSON.stringify(server.document)
  );
}

export function validationIsFresh(plan: DispatchPlan): boolean {
  return plan.validation !== null && plan.validated_version === plan.version;
}

export function planPhase(plan: DispatchPlan, dirty: boolean, request: RequestState): PlanPhase {
  if (request === "publishing" || plan.status === "publishing") return "publishing";
  if (plan.status === "published") return "published";
  if (plan.status === "partially_published") return "partially_published";
  if (plan.status === "closed") return "closed";
  if (request === "validating") return "validating";
  if (dirty) return "modified";
  if (plan.status === "publish_failed") return "publish_failed";
  const v = plan.validation;
  if (!v || !validationIsFresh(plan)) return "draft";
  if (v.status === "error") return "validation_error";
  return v.counts.blocking > 0 ? "invalid" : "valid";
}

// Publishing needs the server's go-ahead for the plan's status (can_publish,
// which covers retrying a partial, failed or stuck publish), plus saved,
// freshly validated, unblocked routes, something to send and no request in
// flight.
export function canPublish(plan: DispatchPlan, dirty: boolean, request: RequestState): boolean {
  const v = plan.validation;
  return (
    plan.can_publish &&
    request === "idle" &&
    !dirty &&
    validationIsFresh(plan) &&
    v !== null &&
    v.status === "ok" &&
    issuesHoldingBack(plan, "blocking").length === 0 &&
    changeCount(plan) > 0
  );
}

// The issues publishing weighs: about the plan, or a route it will send. A
// sent route that isn't changing is with its driver as it is, so its issues
// don't count (the server's rule too).
export function issuesHoldingBack(plan: DispatchPlan, severity: "blocking" | "warning") {
  const sent = Object.entries(plan.publication?.routes ?? {})
    .filter(([, r]) => r.status === "sent")
    .map(([rid]) => rid);
  const settled = new Set(sent.filter((rid) => !plan.changes.update.includes(rid)));
  return (plan.validation?.issues ?? []).filter(
    (i) => i.severity === severity && !(i.route_id && settled.has(i.route_id)),
  );
}

// How many routes publishing would create, update or withdraw (server's view).
export function changeCount(plan: DispatchPlan): number {
  const c = plan.changes;
  return c.create.length + c.update.length + c.withdraw.length;
}

// Routes that still need to reach drivers (all of them before the first
// publish). Only fleet routes on a roster truck are sent; 3PL lanes go to the
// 3PL carrier. The server enforces the same rule (eligible_route_ids).
export function unsentRouteIds(plan: DispatchPlan): string[] {
  const pub = plan.publication?.routes ?? {};
  const onRoster = new Set(plan.roster.vehicles.map((v) => v.id));
  return plan.document.routes
    .filter(
      (r) =>
        r.orders.length > 0 &&
        r.carrier !== "3pl" &&
        r.vehicle_id !== null &&
        onRoster.has(r.vehicle_id) &&
        pub[r.route_id]?.status !== "sent",
    )
    .map((r) => r.route_id);
}
